import * as React from "react"
import { Box, Button, Flex, Text, Avatar, Tooltip, Stack } from "@chakra-ui/react"
import { TbArrowBigDownLineFilled, TbArrowBigUpLineFilled } from "react-icons/tb"
import { useOrgs } from "./providers/OrgProvider"
import { ZoneEntity } from "../lib/types/zone"
import { createBboxFromBounds, getZoneBounds } from "../lib/helpers/map"
import { calcTooltipLabel } from "../lib/helpers/thresholds"
import { useThresholdsColors } from "../lib/hooks/useThresholdsColors"
import { useMapBBox } from "./providers/MapBBoxProvider"

interface BikesMapZoneCardProps {
  zone: any
  bgColor: string
  setSelectedZone: React.Dispatch<React.SetStateAction<ZoneEntity | undefined>>
}

export const BikesMapZoneCard: React.FC<BikesMapZoneCardProps> = ({ zone, setSelectedZone, bgColor }) => {
  const { currentOrgs } = useOrgs()
  const { setCurrentBBox } = useMapBBox()

  const handleZoneClick = (zone: ZoneEntity) => {
    setSelectedZone((prevZone: ZoneEntity) => (prevZone?.uuid === zone.uuid ? undefined : zone))
    const bounds = getZoneBounds(zone)
    if (bounds) {
      setCurrentBBox(createBboxFromBounds(bounds))
    }
  }

  const zoneOrg = currentOrgs.find(({ url }) => url === zone.organization)!

  const tooltipLabel = calcTooltipLabel({
    minThresh: zone.low_threshold,
    maxThresh: zone.high_threshold,
    val: zone.bicycle_count,
  })

  const thresholdColor = useThresholdsColors({
    minThresh: zone.low_threshold,
    maxThresh: zone.high_threshold,
    val: zone.bicycle_count,
  })

  return (
    <Tooltip label={tooltipLabel} placement="right" hasArrow>
      <Button
        h="min-content"
        w="100%"
        display="flex"
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        onClick={() => handleZoneClick(zone)}
        variant="unstyled"
        _hover={{
          bg: "gray.100",
        }}
        p={3}
        px={6}
        borderRadius="lg"
        borderColor={thresholdColor ? `${thresholdColor}96` : bgColor}
        bg={thresholdColor ? `${thresholdColor}19` : bgColor}
        borderStyle="solid"
        borderWidth={1}
      >
        <Flex align="center">
          {currentOrgs.length > 1 && zoneOrg && (
            <Tooltip label={zoneOrg.name}>
              <Avatar name={zoneOrg.name} src={zoneOrg.icon} size="xs" />
            </Tooltip>
          )}
          <Box justifyContent="start" alignItems="start" ml={2}>
            <Text fontWeight="700" textAlign="left">
              {zone.name}
            </Text>
            <Stack isInline fontSize="0.7rem" fontWeight="normal">
              {zone.low_threshold && <Text>Low threshold: {zone.low_threshold}</Text>}
              {zone.high_threshold && <Text>High threshold: {zone.high_threshold}</Text>}
            </Stack>
          </Box>
        </Flex>
        <Stack isInline direction="row" justifyContent="center" alignItems="center">
          {zone.low_threshold && zone.bicycle_count < zone.low_threshold && <TbArrowBigDownLineFilled />}
          {zone.high_threshold && zone.bicycle_count > zone.high_threshold && <TbArrowBigUpLineFilled />}
          <Text>{zone.bicycle_count ?? 0}</Text>
        </Stack>
      </Button>
    </Tooltip>
  )
}
